import { Alert, Button, FileIcon, FilePreview } from '@components'
import { PermissionSet, useBusinessContext, usePermission } from '@context'
import { useLocalStorage } from '@hooks'
import { attachFiles, fetchSuggestedAttachments, IDocument, IFile } from '@query'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPaperclip } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'

interface Props {
  document: IDocument
}

export const AttachmentSuggestions: React.FC<Props> = ({ document }) => {
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const hideStorageKey = `${slug}.document.${document.id}.hideAttachmentSuggestions`
  const [isHidden] = useLocalStorage<boolean | 'dismissed'>(hideStorageKey, false)
  const isDismissed = isHidden === true || isHidden === 'dismissed'
  const hasBookkeepingFilesPermission = usePermission(PermissionSet.BOOKKEEPING_FILES)

  const isSuggestionsEnabled = useMemo(() => {
    return (
      !(document.attachment_ids?.length > 0) &&
      !document.attachment_exempt &&
      !document.is_locked &&
      !isDismissed &&
      hasBookkeepingFilesPermission
    )
  }, [
    document.attachment_ids,
    document.attachment_exempt,
    document.is_locked,
    hasBookkeepingFilesPermission,
    isDismissed
  ])

  const { data } = useQuery({
    queryKey: [slug, 'documents', document.id, 'suggested_files'],
    queryFn: () => fetchSuggestedAttachments({ slug, documentId: document.id }),
    ...{ enabled: isSuggestionsEnabled }
  })
  const suggestions = data ? data.slice(0, 3) : []
  const hideSuggestions = suggestions.length === 0 || isDismissed || !isSuggestionsEnabled

  return (
    <Alert
      type="magic"
      isVisible={!hideSuggestions}
      dismissable
      dismissableKeyDotPath={hideStorageKey}
      title={t('document.suggestions.title')}
      description={
        <StyledContent>
          <StyledBody>{t('document.suggestions.description')}</StyledBody>
          {suggestions.map(attachment => (
            <Suggestion
              key={`suggestion-${attachment.id}`}
              documentId={document.id}
              attachment={attachment}
            />
          ))}
        </StyledContent>
      }
    />
  )
}

interface SuggestionProps {
  documentId: number
  attachment: IFile
}

const Suggestion: React.FC<SuggestionProps> = ({ attachment, documentId }) => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: () => attachFiles({ slug, documentId }, [attachment.id]),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug, 'documents', documentId] })
      }
    }
  })

  return (
    <StyledSuggestion>
      <FilePreview fileId={attachment.id}>
        <div className="file-icon-wrapper">
          <FileIcon
            size={32}
            filetype={attachment.type}
            filename={attachment.name}
            thumb={attachment.file}
          />
        </div>
      </FilePreview>
      <Button
        icon={<FaPaperclip />}
        onClick={() => {
          mutate()
        }}
        showSpinner={isLoading}
        disabled={isLoading}
        isSecondary
      >
        {t('document.suggestions.attach')}
      </Button>
    </StyledSuggestion>
  )
}

const StyledBody = styled.p`
  color: ${({ theme }) => theme.colors.neutralBlack};
  margin: 0;
`

const StyledSuggestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  .file-icon-wrapper > * {
    margin: 0;
  }
`

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
